'use client';

import { createContext, useContext, useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import { Toaster } from 'sonner';
import { Button, PageLoading } from '@/ui';
import { cn } from '@/lib/cn';
import { ApiError } from '@/lib/api/client';
import { portalApi, type PortalContact } from '@/lib/api/portal';
import { useBrandTitle } from '@/lib/api/brand';

interface PortalSession {
  contact: PortalContact;
  hasPassword: boolean;
  brandName: string;
  refresh: () => Promise<void>;
}

const PortalSessionContext = createContext<PortalSession | null>(null);

export function usePortalSession(): PortalSession {
  const session = useContext(PortalSessionContext);
  if (!session) throw new Error('usePortalSession must be used inside PortalShell');
  return session;
}

// One-page dashboard: everything money/contract/request lives on Home. Only
// the occasional task flows keep their own destinations.
const NAV_ITEMS = [
  { href: '/portal', label: 'Home' },
  { href: '/portal/domain', label: 'Domain' },
];

export function PortalShell({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const pathname = usePathname();
  const [session, setSession] = useState<{ contact: PortalContact; hasPassword: boolean; brandName: string } | null>(null);

  async function loadSession(): Promise<void> {
    try {
      const me = await portalApi.me();
      setSession(me);
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        router.replace('/portal/login');
        return;
      }
      throw error;
    }
  }

  useEffect(() => {
    void loadSession();
     
  }, []);

  useBrandTitle('Customer portal', session?.brandName ?? '');

  async function onSignOut(): Promise<void> {
    await portalApi.logout();
    router.replace('/portal/login');
  }

  if (!session) {
    return <PageLoading />;
  }

  return (
    <PortalSessionContext.Provider value={{ ...session, refresh: loadSession }}>
      <div className="min-h-screen bg-zinc-950">
        {/* Same toaster the staff shell mounts — portal pages (Domain)
            fire toasts too, and without this they were silently dropped. */}
        <Toaster
          theme="dark"
          position="top-right"
          toastOptions={{
            style: {
              background: '#161619',
              border: '1px solid #3a3a42',
              borderRadius: '0.75rem 0.2rem 0.75rem 0.2rem',
              color: '#e9e9eb',
              fontSize: '13px',
            },
          }}
        />
        <header className="border-b border-zinc-800 bg-zinc-900">
          <div className="mx-auto flex h-14 w-full max-w-4xl items-center gap-6 px-5">
            <span className="text-sm font-semibold text-zinc-50">{session.brandName}</span>
            <nav className="flex flex-1 items-center gap-1">
              {NAV_ITEMS.map((item) => {
                const active = pathname === item.href;
                return (
                  <Link
                    key={item.href}
                    href={item.href}
                    className={cn(
                      'corner-brand-sm px-2.5 py-1.5 text-sm transition-colors',
                      active ? 'bg-accent/15 text-zinc-50' : 'text-zinc-400 hover:text-zinc-100'
                    )}
                  >
                    {item.label}
                  </Link>
                );
              })}
            </nav>
            <span className="hidden text-sm text-zinc-400 sm:block">{session.contact.companyName}</span>
            <Link
              href="/portal/account"
              className={cn(
                'corner-brand-sm px-2.5 py-1.5 text-sm transition-colors',
                pathname === '/portal/account' ? 'bg-accent/15 text-zinc-50' : 'text-zinc-400 hover:text-zinc-100'
              )}
            >
              Account
            </Link>
            <Button variant="secondary" size="sm" onClick={() => void onSignOut()}>
              Sign out
            </Button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-4xl px-5 py-6">{children}</main>
      </div>
    </PortalSessionContext.Provider>
  );
}
